import { z } from "zod";
import { decodeFunctionData, getAddress, type Address, type Hex } from "viem";
import { findPendingMint, findRewardRedeemed, hasNftBurn, ponsRewardNftAbi } from "../contracts/pons-reward-nft";
import { currentChainId, sendPreparedTransaction, waitForTransactionReceipt, type Eip1193Provider, type WalletTransactionReceipt } from "./eip1193";
import { readRpc } from "./read-rpc";
import { ContextChangedError } from "./wallet-operation";
import type { PreparedNftTransaction } from "./transaction-preflight";
import { FinalizationNotNeeded, verifyFinalization, verifyAtomicMint, readNftState } from "./nft-lifecycle";
import { ELYSIUM } from "../config/elysium";
import { observeElysium } from "./elysium-readiness";
import { reconcileRedemption } from "./redemption-reconciliation";
import { redemptionError } from "./redemption-diagnostics";
import { taskMintReceiptState } from "../features/social-tasks/contracts/task-mint-receipt";
import { assertTaskMintCall } from "../features/social-tasks/wallet/task-mint-preflight";
import { ContractReadError } from "../contracts/contract-errors";
import { referralMilestoneSchema } from "../features/referrals/api/referral-schemas";
import { REFERRAL_RECEIPT_PROTOCOL, REFERRAL_FINALIZED_RECEIPT_PROTOCOL, sameReferralIdentity, type ReferralMintIdentity } from "../features/referrals/contracts/referral-mint";
import { referralMintReceipt } from "../features/referrals/contracts/referral-mint-receipt";
import { assertReferralMintCall } from "../features/referrals/wallet/referral-mint-preflight";

const address = z.string().regex(/^0x[0-9a-fA-F]{40}$/);
const hash = z.string().regex(/^0x[0-9a-fA-F]{64}$/);
const socialClaimSchema = z.object({ account: address, chainId: z.number().int().positive().safe(),
  collection: address, environment: z.string().min(1) });
const referralClaimSchema = socialClaimSchema.extend({ milestone: referralMilestoneSchema });
export type ReferralClaim = z.infer<typeof referralClaimSchema>;
const transactionSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(["MINT", "REDEEM", "FINALIZE"]),
  account: address,
  chainId: z.number().int().positive(),
  collection: address,
  sourceToken: address,
  environment: z.string().min(1),
  tokenId: z.string().regex(/^\d+$/).nullable(),
  mintIdentity: z.object({ proofKey: hash, authorizationId: hash,
    tierUsd: z.string().regex(/^\d+$/), feeWei: z.string().regex(/^\d+$/),
    route: z.enum(["SOCIAL_TASK", "REFERRAL"]).optional(), policyHash: hash.optional(), policyVersion: z.string().regex(/^(0|[1-9][0-9]*)$/).optional(),
    milestone: referralMilestoneSchema.optional(), instanceId: hash.optional(), evidenceId: z.string().trim().min(1).optional(),
    receiptProtocol: z.enum([REFERRAL_RECEIPT_PROTOCOL, REFERRAL_FINALIZED_RECEIPT_PROTOCOL]).optional(),
  }).strict().refine(identity => identity.route !== "SOCIAL_TASK" ||
    identity.tierUsd === "0" && identity.feeWei === "0" && !!identity.policyHash && identity.policyVersion !== undefined, "Invalid task mint identity").optional(),
  redeemMinimumWei: z.string().regex(/^\d+$/).optional(),
  redeemDeadline: z.number().int().positive().safe().optional(),
  redeemSubmittedDeadline: z.string().regex(/^(0|[1-9][0-9]*)$/).optional(),
  redeemRouteRevision: z.string().min(1).optional(),
  redemptionRecheck: z.boolean().optional(),
  receiptBlockNumber: z.string().regex(/^0x(?:0|[1-9a-f][0-9a-f]*)$/i).optional(),
  receiptBlockHash: hash.optional(),
  payoutWei: z.string().regex(/^\d+$/).optional(),
  hash: hash.nullable(),
  phase: z.enum(["PREPARING", "AWAITING_SIGNATURE", "PENDING", "UNCONFIRMED", "CONFIRMED", "REVERTED",
    "REJECTED", "FAILED", "CANCELLED", "REVIEW_REQUIRED", "SAMPLING_REQUIRED", "SAMPLING_EXHAUSTED", "NOT_NEEDED", "ORPHANED"]),
  message: z.string(),
  createdAt: z.number(),
}).refine(item => {
  const identity = item.mintIdentity;
  if (identity?.route === "REFERRAL") return item.kind === "MINT" && identity.milestone !== undefined &&
    identity.tierUsd === String(identity.milestone) && identity.feeWei === "0" && !!identity.policyHash &&
    identity.policyVersion !== undefined && !!identity.instanceId && !!identity.evidenceId && !!identity.receiptProtocol;
  return !identity || identity.milestone === undefined && identity.instanceId === undefined &&
    identity.evidenceId === undefined && identity.receiptProtocol === undefined;
}, "Invalid or downgraded referral mint identity");

export type TrackedTransaction = z.infer<typeof transactionSchema>;
export type TransactionScope = Pick<TrackedTransaction, "account" | "chainId" | "collection" | "environment">;
export type TransactionContext = TransactionScope & Pick<TrackedTransaction, "kind" | "sourceToken" | "tokenId" | "mintIdentity" | "redeemMinimumWei" | "redeemDeadline" | "redeemRouteRevision">;
export type PreparedTransaction = PreparedNftTransaction;
export type TransactionSnapshot = { items: readonly TrackedTransaction[]; redemptions: readonly TrackedTransaction[];
  referralClaims: readonly ReferralClaim[]; referralClaimStorageWarning: string | null;
  socialClaims: readonly TransactionScope[]; socialClaimStorageWarning: string | null; storageWarning: string | null };
export const TRANSACTION_STORAGE_KEY = "greenwood.transactions.v1";
export const REDEMPTION_STORAGE_KEY = "greenwood.redemptions.v1";
export const SOCIAL_CLAIM_STORAGE_KEY = "greenwood.social-claims.v1";
export const REFERRAL_CLAIM_STORAGE_KEY = "greenwood.referral-claims.v1";

export function transactionUnresolved(item: TrackedTransaction): boolean {
  return ["PREPARING", "AWAITING_SIGNATURE", "PENDING", "UNCONFIRMED", "REVIEW_REQUIRED"].includes(item.phase);
}

export function matchesTransactionScope(item: TransactionScope, scope: TransactionScope): boolean {
  return same(item.account, scope.account) && item.chainId === scope.chainId &&
    same(item.collection, scope.collection) && item.environment === scope.environment;
}

// Stores only transaction identities/results, never session tokens, signatures or calldata.
// sessionStorage intentionally scopes recovery to this browser tab (including reload).
export class TransactionTracker {
  private snapshot: TransactionSnapshot = { items: [], redemptions: [], socialClaims: [], socialClaimStorageWarning: null,
    referralClaims: [], referralClaimStorageWarning: null, storageWarning: null };
  private listeners = new Set<() => void>();
  private watchers = new Map<string, { abort: AbortController; promise: Promise<void> }>();
  private savedSocialClaimCount = 0;
  private savedReferralClaimCount = 0;

  constructor(private readonly storage: Pick<Storage, "getItem" | "setItem">,
    private readonly waitForReceipt = waitForTransactionReceipt) {
    try {
      const raw = storage.getItem(TRANSACTION_STORAGE_KEY);
      if (raw) {
        const items = z.array(transactionSchema).parse(JSON.parse(raw));
        this.snapshot = { ...this.snapshot, items: items.map((item) => transactionUnresolved(item) ? {
          ...item, phase: "UNCONFIRMED", message: item.hash ?
            "Saved transaction. Connect the same wallet on its network to check confirmation." :
            "The wallet request was interrupted before a hash was recorded. Check wallet activity before trying again.",
        } : item) };
      }
      const savedRedemptions = storage.getItem(REDEMPTION_STORAGE_KEY);
      const redemptions = savedRedemptions ? z.array(transactionSchema).parse(JSON.parse(savedRedemptions)) : [];
      if (redemptions.some(item => item.kind !== "REDEEM" || item.phase !== "CONFIRMED" || !item.hash || !item.tokenId || !item.payoutWei || BigInt(item.payoutWei) <= 0n)) {
        throw new Error("Invalid saved redemption");
      }
      this.snapshot.redemptions = redemptionLedger(this.snapshot.items, redemptions);
    } catch {
      this.snapshot = { ...this.snapshot, storageWarning: "Saved transaction data could not be read. Check wallet activity before sending a new transaction." };
    }
    // Load independently: an unreadable history must not erase an existing claim ledger.
    try {
      const rawClaims = storage.getItem(SOCIAL_CLAIM_STORAGE_KEY);
      if (rawClaims) this.snapshot.socialClaims = socialClaimLedger([], z.array(socialClaimSchema).parse(JSON.parse(rawClaims)));
      this.savedSocialClaimCount = this.snapshot.socialClaims.length;
    } catch {
      this.snapshot.socialClaimStorageWarning = "Saved social claim data could not be read. Check wallet activity before another social mint.";
    }
    // Old tabs have no claim store. Preserve verified legacy claims before history can be dismissed/trimmed.
    const migrated = socialClaimLedger(this.snapshot.items, this.snapshot.socialClaims);
    if (migrated.length !== this.snapshot.socialClaims.length) {
      this.snapshot.socialClaims = migrated;
      try {
        storage.setItem(SOCIAL_CLAIM_STORAGE_KEY, JSON.stringify(migrated));
        this.savedSocialClaimCount = migrated.length;
      }
      catch { this.snapshot.socialClaimStorageWarning = "Social claim state cannot be saved. Keep this tab open until recovery storage is available."; }
    }
    try {
      const raw = storage.getItem(REFERRAL_CLAIM_STORAGE_KEY);
      if (raw) this.snapshot.referralClaims = referralClaimLedger([], z.array(referralClaimSchema).parse(JSON.parse(raw)));
      this.savedReferralClaimCount = this.snapshot.referralClaims.length;
    } catch { this.snapshot.referralClaimStorageWarning = "Saved referral claims could not be read. Check wallet activity before another referral mint."; }
    const referralClaims = referralClaimLedger(this.snapshot.items, this.snapshot.referralClaims);
    if (referralClaims.length !== this.savedReferralClaimCount) {
      this.snapshot.referralClaims = referralClaims;
      try {
        storage.setItem(REFERRAL_CLAIM_STORAGE_KEY, JSON.stringify(referralClaims));
        this.savedReferralClaimCount = referralClaims.length;
      } catch { this.snapshot.referralClaimStorageWarning = "Referral claims cannot be saved. Keep this tab open until recovery storage is available."; }
    }
  }

  getSnapshot = (): TransactionSnapshot => this.snapshot;
  referralClaimed = (scope: TransactionScope, milestone: ReferralClaim["milestone"]): boolean =>
    this.snapshot.referralClaims.some(claim => claim.milestone === milestone && matchesTransactionScope(claim, scope));
  /** Current authenticated authorization response only; not a minted-token receipt. */
  recordReferralClaim(scope: TransactionScope, milestone: ReferralClaim["milestone"]) {
    const claim = referralClaimSchema.parse({ ...scope, milestone });
    this.publish(this.snapshot.items, [], [claim]);
  }
  socialTaskClaimed = (scope: TransactionScope): boolean => this.snapshot.socialClaims.some(claim => matchesTransactionScope(claim, scope));
  redeemed = (context: TransactionScope & { sourceToken: string; tokenId: string | null }): TrackedTransaction | undefined =>
    this.snapshot.redemptions.find(item => matchesTransactionScope(item, context) && same(item.sourceToken, context.sourceToken) && item.tokenId === context.tokenId);
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  async execute(context: TransactionContext, provider: Eip1193Provider,
    prepare: () => Promise<PreparedTransaction>, guard?: { assertCurrent(): void; signal: AbortSignal }): Promise<TrackedTransaction> {
    guard?.assertCurrent();
    transactionSchema.parse({ ...context, id: "validation", createdAt: Date.now(), hash: null, phase: "PREPARING", message: "" });
    if (context.mintIdentity?.route === "REFERRAL" && (this.referralClaimed(context, context.mintIdentity.milestone!) ||
      this.snapshot.storageWarning || this.snapshot.referralClaimStorageWarning)) {
      throw new Error("Referral milestone already claimed or recovery storage unavailable. Check wallet activity.");
    }
    if (context.kind === "MINT" && context.mintIdentity?.route === "SOCIAL_TASK" && this.socialTaskClaimed(context)) {
      throw new Error("This wallet has already claimed its social task NFT.");
    }
    if (context.kind === "REDEEM" && (this.redeemed(context) || this.snapshot.storageWarning)) {
      throw new Error(this.redeemed(context) ? "Redemption and burn are already verified. Wait for inventory indexing." :
        "Recovery storage is unavailable. Check wallet activity before another redemption.");
    }
    if (this.snapshot.items.some((item) => matchesTransactionScope(item, context) && transactionUnresolved(item))) {
      throw new Error("Check the existing transaction before sending another one");
    }
    const item: TrackedTransaction = { ...context, id: crypto.randomUUID(), createdAt: Date.now(), hash: null,
      phase: "PREPARING", message: "Checking eligibility, transaction details and network." };
    this.publish([item, ...this.snapshot.items]);
    let sendRequested = false;
    let reviewedSocialCall = false;
    let reviewedReferralCall = false;
    let walletWaitTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      const call = await prepare();
      guard?.assertCurrent();
      if (!same(call.account, context.account) || call.chainId !== context.chainId || !same(call.to, context.collection)) {
        throw new Error("Prepared transaction does not match the tracked wallet, network and collection");
      }
      let redeemSubmittedDeadline: string | undefined;
      if (context.mintIdentity?.route === "REFERRAL" || call.referralMint) {
        const decoded = assertReferralMintCall(call);
        if (context.kind !== "MINT" || !decoded.identity || context.mintIdentity?.route !== "REFERRAL" ||
          !sameReferralIdentity(decoded.identity, context.mintIdentity) ||
          !same(decoded.authorization.sourceToken, context.sourceToken) || call.referralMint?.evidence.environment !== context.environment) {
          throw new Error("Referral call does not match tracked authorization");
        }
        reviewedReferralCall = true;
      }
      if (context.mintIdentity?.route === "SOCIAL_TASK" || call.taskMint) {
        const identity = context.mintIdentity;
        assertTaskMintCall(call);
        if (context.kind !== "MINT" || identity?.route !== "SOCIAL_TASK" || !call.taskMint ||
          !same(call.taskMint.proofKey, identity.proofKey) || !same(call.taskMint.authorizationId, identity.authorizationId) ||
          !same(call.taskMint.policyHash, identity.policyHash ?? "") || call.taskMint.policyVersion !== identity.policyVersion ||
          !same(call.taskMint.sourceToken, context.sourceToken)) {
          throw new Error("Task mint call does not match tracked authorization");
        }
        reviewedSocialCall = true;
      }
      if (context.kind === "REDEEM") {
        const decoded = decodeFunctionData({ abi: ponsRewardNftAbi, data: call.data });
        if ((decoded.functionName !== "redeem" && decoded.functionName !== "redeemWithFees") ||
          decoded.args[0].toString() !== context.tokenId ||
          context.redeemMinimumWei !== undefined && decoded.args[1].toString() !== context.redeemMinimumWei ||
          context.redeemDeadline !== undefined && decoded.args[2] !== BigInt(context.redeemDeadline) ||
          decoded.functionName === "redeemWithFees" && (!call.redeemIdentity ||
            !same(call.redeemIdentity.route.sourceToken, context.sourceToken) ||
            call.redeemIdentity.route.deploymentRevision !== context.redeemRouteRevision) ||
          decoded.functionName === "redeem" && context.redeemRouteRevision !== undefined &&
            (!call.directRedeemIdentity || !same(call.directRedeemIdentity.route.sourceToken, context.sourceToken) ||
              call.directRedeemIdentity.route.deploymentRevision !== context.redeemRouteRevision)) {
          throw new Error("Prepared redemption does not match the tracked review");
        }
        redeemSubmittedDeadline = decoded.args[2].toString();
      }
      const transactionHash = await sendPreparedTransaction(provider, call, () => guard?.assertCurrent(), () => {
        this.update(item.id, { phase: "AWAITING_SIGNATURE", message: "Confirm or reject the transaction in your wallet.",
          ...(redeemSubmittedDeadline !== undefined ? { redeemSubmittedDeadline } : {}) });
        // A synchronous subscriber may invalidate the context while publishing.
        guard?.assertCurrent();
        sendRequested = true;
        walletWaitTimer = setTimeout(() => {
          if (this.find(item.id)?.phase === "AWAITING_SIGNATURE") this.update(item.id, {
            message: "The wallet has not returned a transaction hash. Open your wallet to approve or reject its pending request. Do not submit again.",
          });
        }, 60_000);
      }, { ...(guard ? { signal: guard.signal } : {}),
        onProgress: message => this.update(item.id, { message }) });
      // Save immediately after broadcast, before attempting any receipt read.
      this.update(item.id, { hash: transactionHash, phase: "PENDING", message: "Transaction sent. Waiting for an on-chain receipt." });
    } catch (error) {
      const alreadyClaimed = !sendRequested && reviewedSocialCall && error instanceof ContractReadError &&
        error.errorName === "TaskMintAlreadyClaimed" && error.contractAddress !== undefined && same(error.contractAddress, context.collection);
      const rejected = sendRequested && isWalletRejection(error);
      const referralClaim = !sendRequested && reviewedReferralCall && error instanceof ContractReadError &&
        error.errorName === "ReferralAlreadyClaimed" && error.contractAddress !== undefined && same(error.contractAddress, context.collection);
      const cancelled = !sendRequested && (error instanceof ContextChangedError || guard?.signal.aborted);
      this.update(item.id, {
        phase: cancelled ? "CANCELLED" : rejected ? "REJECTED" : sendRequested ? "UNCONFIRMED" :
          item.kind === "FINALIZE" && error instanceof FinalizationNotNeeded ? "NOT_NEEDED" : "FAILED",
        message: cancelled ? new ContextChangedError().message : rejected ? "Wallet request rejected. No transaction hash was returned." : sendRequested ?
          "The wallet did not return a transaction hash. Check its activity before trying again." :
          context.kind === "REDEEM" ? redemptionError(error, "Transaction preparation could not be completed.") : errorText(error),
      }, alreadyClaimed ? [context] : [], referralClaim ? [{ ...context, milestone: context.mintIdentity!.milestone! }] : []);
      return this.find(item.id)!;
    } finally { clearTimeout(walletWaitTimer); }
    if (guard?.signal.aborted) {
      this.update(item.id, { phase: "UNCONFIRMED", message: "Transaction sent before the context changed. Reconnect the original wallet and network to check confirmation." });
      return this.find(item.id)!;
    }
    await this.check(item.id, provider, context);
    return this.find(item.id)!;
  }

  check(id: string, provider: Eip1193Provider, scope: TransactionScope): Promise<void> {
    const item = this.find(id) ?? this.snapshot.redemptions.find(entry => entry.id === id);
    if (!item || !matchesTransactionScope(item, scope)) return Promise.reject(new Error("Connect the transaction owner wallet and configured collection"));
    const recheck = item.kind === "REDEEM" && (item.phase === "CONFIRMED" || item.redemptionRecheck === true);
    if (!item.hash || (!transactionUnresolved(item) && !recheck)) return Promise.resolve();
    const existing = this.watchers.get(id);
    if (existing && !existing.abort.signal.aborted) return existing.promise;
    const abort = new AbortController();
    if (!this.find(id)) this.publish([item, ...this.snapshot.items]);
    const promise = this.monitor(item, provider, abort.signal, recheck).finally(() => {
      if (this.watchers.get(id)?.abort === abort) this.watchers.delete(id);
    });
    this.watchers.set(id, { abort, promise });
    return promise;
  }

  stopWatching(): void {
    for (const watcher of this.watchers.values()) watcher.abort.abort();
    this.watchers.clear();
  }

  dismiss(id: string): void {
    const item = this.find(id);
    if (!item) return;
    // A hash with an unknown outcome must remain tracked. A hashless wallet
    // interruption can only be acknowledged explicitly after checking the wallet.
    if (transactionUnresolved(item) && (item.hash || item.phase !== "UNCONFIRMED")) return;
    this.publish(this.snapshot.items.filter((entry) => entry.id !== id));
  }

  private async monitor(item: TrackedTransaction, provider: Eip1193Provider, signal: AbortSignal, recheck = false): Promise<void> {
    this.update(item.id, { phase: "PENDING", message: recheck ? "Rechecking the redemption receipt and current chain state. No transaction will be sent." :
      "Transaction sent. Checking on-chain confirmation.", ...(recheck ? { redemptionRecheck: true, payoutWei: undefined } : {}) });
    try {
      if (item.chainId === ELYSIUM.chainId) await observeElysium(provider, () => signal.throwIfAborted(), { signal });
      const receipt = recheck ? await reconcileRedemption(provider, item, signal) :
        await this.waitForReceipt(provider, item.hash as Hex, { signal, expectedChainId: item.chainId });
      if (signal.aborted) return;
      if (receipt === null) {
        this.update(item.id, { phase: "ORPHANED", payoutWei: undefined, redemptionRecheck: false,
          message: "The previous redemption is no longer confirmed, its deadline has expired, and the owner still holds a finalized NFT. Prepare a fresh review to redeem; nothing was resent." });
        return;
      }
      if (!same(receipt.transactionHash, item.hash!) || (receipt.status !== "0x0" && receipt.status !== "0x1")) {
        this.update(item.id, { phase: "REVIEW_REQUIRED", message: "Receipt identity is invalid. Check confirmation; do not resend." });
        return;
      }
      // Reverts need the same canonical evidence as successful receipts. An
      // orphaned receipt cannot release the unresolved transaction's send lock.
      await verifyReceiptBlock(provider, receipt, item.chainId, signal);
      if (signal.aborted) return;
      if (receipt.status === "0x0") {
        this.update(item.id, { phase: "REVERTED", payoutWei: undefined, redemptionRecheck: false,
          ...(recheck ? { receiptBlockNumber: receipt.blockNumber, receiptBlockHash: receipt.blockHash } : {}),
          message: "Transaction reverted on-chain. Network gas fees may still apply." });
        return;
      }
      if (item.kind === "FINALIZE") {
        const result = item.tokenId === null ? null : await verifyFinalization(provider, receipt, {
          chainId: item.chainId, collection: getAddress(item.collection), tokenId: BigInt(item.tokenId), sourceToken: item.sourceToken,
        }, () => signal.throwIfAborted(), { signal });
        if (signal.aborted) return;
        this.update(item.id, result ?? { phase: "REVIEW_REQUIRED",
          message: "Receipt succeeded, but finalization or sampling could not be verified. Check confirmation; do not resend." });
        return;
      }
      if (item.mintIdentity?.route === "REFERRAL") {
        const result = referralMintReceipt(receipt, { chainId: item.chainId, collection: item.collection, account: item.account,
          sourceToken: item.sourceToken, identity: item.mintIdentity as ReferralMintIdentity });
        const atomic = item.chainId !== ELYSIUM.chainId || result && await verifyAtomicMint(provider, receipt, {
          chainId: item.chainId, collection: getAddress(item.collection), tokenId: BigInt(result.tokenId), sourceToken: item.sourceToken, wallet: item.account,
        }, () => signal.throwIfAborted(), { signal });
        this.update(item.id, result && atomic ? { phase: "CONFIRMED", tokenId: result.tokenId,
          receiptBlockNumber: receipt.blockNumber, receiptBlockHash: receipt.blockHash,
          message: `Referral ${item.mintIdentity.milestone} NFT minted. ${result.rewardStatus === "FINALIZED" ? "Reward finalized." : "Reward finalization pending."} Inventory indexing is pending; chain finality is not established here.` } :
          { phase: "REVIEW_REQUIRED", message: "Referral mint identity, transfer or reward could not be verified. Check confirmation; do not resend." });
        return;
      }
      const event = item.kind === "MINT" ? findPendingMint(receipt, getAddress(item.collection)) :
        findRewardRedeemed(receipt, getAddress(item.collection));
      const owner = event && ("wallet" in event ? event.wallet : event.owner);
      const expected = item.mintIdentity;
      const mintMismatch = item.kind === "MINT" && expected && (!event || !("proofKey" in event) ||
        !same(event.proofKey, expected.proofKey) || !same(event.authorizationId, expected.authorizationId) ||
        event.selectedTierUsd.toString() !== expected.tierUsd || event.mintFeeWei.toString() !== expected.feeWei ||
        expected.route === "SOCIAL_TASK" && (!same(event.rewardPolicyHash, expected.policyHash!) ||
          event.rewardPolicyVersion.toString() !== expected.policyVersion));
      const redeemMismatch = item.kind === "REDEEM" && (!event || !("payoutWei" in event) || event.payoutWei <= 0n ||
        !/^0x(?:0|[1-9a-f][0-9a-f]*)$/i.test(receipt.blockNumber) || !/^0x[0-9a-f]{64}$/i.test(receipt.blockHash) ||
        (item.redeemMinimumWei !== undefined && event.payoutWei < BigInt(item.redeemMinimumWei)) ||
        !hasNftBurn(receipt, item.collection, item.account, item.tokenId ?? ""));
      if (!event || !owner || !same(owner, item.account) || !same(event.sourceToken, item.sourceToken) ||
        mintMismatch || redeemMismatch ||
        (item.kind === "REDEEM" && event.tokenId.toString() !== item.tokenId)) {
        this.update(item.id, { phase: "REVIEW_REQUIRED", message: "Receipt succeeded, but the expected NFT event could not be verified. Check wallet activity; do not resend." });
        return;
      }
      if (item.kind === "REDEEM" && item.chainId === ELYSIUM.chainId) {
        const state = await readNftState(provider, { chainId: item.chainId, collection: getAddress(item.collection), tokenId: BigInt(item.tokenId!),
          block: { number: receipt.blockNumber, hash: receipt.blockHash } }, () => signal.throwIfAborted(), { signal });
        if (state.status !== 4 || !same(state.sourceToken, item.sourceToken)) {
          this.update(item.id, { phase: "REVIEW_REQUIRED", message: "Redemption events did not match canonical burned state. Keep the hash; do not resend." }); return;
        }
      }
      const taskState = item.mintIdentity?.route === "SOCIAL_TASK" ?
        taskMintReceiptState(receipt, item.collection, item.account, event.tokenId, item.mintIdentity.policyHash!) : undefined;
      if (taskState === null) {
        this.update(item.id, { phase: "REVIEW_REQUIRED", message: "Task mint receipt did not verify the NFT transfer or reward policy. Check confirmation; do not resend." });
        return;
      }
      if (item.kind === "MINT" && item.chainId === ELYSIUM.chainId && !await verifyAtomicMint(provider, receipt, {
        chainId: item.chainId, collection: getAddress(item.collection), tokenId: event.tokenId, sourceToken: item.sourceToken, wallet: item.account,
      }, () => signal.throwIfAborted(), { signal })) {
        this.update(item.id, { phase: "REVIEW_REQUIRED", message: "Mint receipt lacks verified atomic finalization. Check confirmation; do not resend." });
        return;
      }
      this.update(item.id, { phase: "CONFIRMED", tokenId: event.tokenId.toString(), message: item.kind === "MINT" ?
        item.chainId === ELYSIUM.chainId ? "NFT minted and reward finalized in this transaction. Inventory indexing and chain finality remain separate." :
        taskState === "FINALIZED" ? "Social task NFT minted and reward finalized. Inventory will update after indexing." :
        taskState === "PENDING_RANDOMNESS" ? "Social task NFT minted. Reward finalization and inventory indexing are pending." :
        "Mint confirmed. The NFT awaits randomness and inventory indexing." :
        "Redemption payout and NFT burn verified. Inventory will update after indexing.",
        ...(item.kind === "REDEEM" ? { receiptBlockNumber: receipt.blockNumber, receiptBlockHash: receipt.blockHash } : {}),
        ...("payoutWei" in event ? { payoutWei: event.payoutWei.toString() } : {}) });
    } catch (error) {
      if (signal.aborted) return;
      this.update(item.id, { phase: "UNCONFIRMED", message: errorText(error).startsWith("WRONG_CHAIN") ?
        `Switch your wallet to chain ${item.chainId}, then check confirmation again.` :
        recheck ? "Redemption reconciliation is incomplete. Its hash remains recoverable and another send stays blocked. Check confirmation again; no transaction was resent." :
          "Confirmation is unavailable or taking longer than expected. This does not mean the transaction failed. Check again; no transaction will be resent." });
    }
  }

  private find(id: string): TrackedTransaction | undefined { return this.snapshot.items.find((item) => item.id === id); }
  private update(id: string, patch: Partial<TrackedTransaction>, claims: readonly TransactionScope[] = [], referralClaims: readonly ReferralClaim[] = []): void {
    this.publish(this.snapshot.items.map((item) => item.id === id ? { ...item, ...patch } : item), claims, referralClaims);
  }
  private publish(items: readonly TrackedTransaction[], claims: readonly TransactionScope[] = [], observedReferralClaims: readonly ReferralClaim[] = []): void {
    const redemptions = redemptionLedger(items, this.snapshot.redemptions);
    const socialClaims = socialClaimLedger(items, this.snapshot.socialClaims, claims);
    const referralClaims = referralClaimLedger(items, this.snapshot.referralClaims, observedReferralClaims);
    let completed = 0;
    const retained = items.filter((item) => transactionUnresolved(item) || ++completed <= 10);
    let storageWarning = this.snapshot.storageWarning;
    let socialClaimStorageWarning = this.snapshot.socialClaimStorageWarning;
    let referralClaimStorageWarning = this.snapshot.referralClaimStorageWarning;
    if (referralClaims.length !== this.savedReferralClaimCount) {
      try {
        this.storage.setItem(REFERRAL_CLAIM_STORAGE_KEY, JSON.stringify(referralClaims));
        this.savedReferralClaimCount = referralClaims.length;
      } catch { referralClaimStorageWarning = "Referral claim state cannot be saved. Keep this tab open and check wallet activity before another referral mint."; }
    }
    if (socialClaims.length !== this.savedSocialClaimCount) {
      try {
        // Claims survive display-history dismissal/trimming; save them before shortening history.
        this.storage.setItem(SOCIAL_CLAIM_STORAGE_KEY, JSON.stringify(socialClaims));
        this.savedSocialClaimCount = socialClaims.length;
      } catch {
        socialClaimStorageWarning = "Social claim state cannot be saved. Keep this tab open until recovery storage is available.";
      }
    }
    try {
      // Preparing has no wallet side effect yet; do not restore it as a sent transaction.
      this.storage.setItem(TRANSACTION_STORAGE_KEY, JSON.stringify(retained.filter((item) => item.phase !== "PREPARING")));
      this.storage.setItem(REDEMPTION_STORAGE_KEY, JSON.stringify(redemptions));
    } catch {
      storageWarning = "Transaction progress cannot be saved in this browser. Keep the hash and this tab open until confirmation.";
    }
    this.snapshot = { items: retained, redemptions, socialClaims, socialClaimStorageWarning, storageWarning, referralClaims, referralClaimStorageWarning };
    for (const listener of this.listeners) listener();
  }
}

async function verifyReceiptBlock(provider: Eip1193Provider, receipt: WalletTransactionReceipt,
  chainId: number, signal: AbortSignal): Promise<void> {
  const options = { signal };
  signal.throwIfAborted();
  if (!/^0x(?:0|[1-9a-f][0-9a-f]*)$/i.test(receipt.blockNumber) ||
    !/^0x[0-9a-f]{64}$/i.test(receipt.blockHash) || BigInt(receipt.blockHash) === 0n) {
    throw new Error("Invalid receipt block identity");
  }
  if (await currentChainId(provider, options) !== chainId) throw new Error(`WRONG_CHAIN:${chainId}`);
  const block = await readRpc(provider, { method: "eth_getBlockByNumber", params: [receipt.blockNumber, false] }, options);
  signal.throwIfAborted();
  if (!block || typeof block !== "object" || !("number" in block) || block.number !== receipt.blockNumber ||
    !("hash" in block) || typeof block.hash !== "string" || !same(block.hash, receipt.blockHash)) {
    throw new Error("Receipt block is no longer canonical. Check confirmation again.");
  }
  if (await currentChainId(provider, options) !== chainId) throw new Error(`WRONG_CHAIN:${chainId}`);
  signal.throwIfAborted();
}

function socialClaimLedger(items: readonly TrackedTransaction[], saved: readonly TransactionScope[],
  observed: readonly TransactionScope[] = []): TransactionScope[] {
  const confirmed = items.filter(item => item.kind === "MINT" && item.phase === "CONFIRMED" &&
    item.mintIdentity?.route === "SOCIAL_TASK" && item.hash !== null && item.tokenId !== null);
  const scopes = new Map<string, TransactionScope>();
  for (const scope of [...saved, ...observed, ...confirmed]) {
    const claim = { account: scope.account.toLowerCase(), chainId: scope.chainId,
      collection: scope.collection.toLowerCase(), environment: scope.environment };
    scopes.set(JSON.stringify([claim.account, claim.chainId, claim.collection, claim.environment]), claim);
  }
  return [...scopes.values()];
}

function referralClaimLedger(items: readonly TrackedTransaction[], saved: readonly ReferralClaim[],
  observed: readonly ReferralClaim[] = []): ReferralClaim[] {
  const confirmed = items.flatMap(item => item.kind === "MINT" && item.phase === "CONFIRMED" &&
    item.mintIdentity?.route === "REFERRAL" && item.hash && item.tokenId !== null && item.mintIdentity.milestone !== undefined ?
    [{ ...item, milestone: item.mintIdentity.milestone }] : []);
  const claims = new Map<string, ReferralClaim>();
  // Claims are once per wallet/milestone across sources; transaction records keep the original source.
  for (const scope of [...saved, ...observed, ...confirmed]) {
    const claim = { account: scope.account.toLowerCase(), chainId: scope.chainId,
      collection: scope.collection.toLowerCase(), environment: scope.environment, milestone: scope.milestone };
    claims.set(JSON.stringify([claim.account, claim.chainId, claim.collection, claim.environment, claim.milestone]), claim);
  }
  return [...claims.values()];
}

function redemptionLedger(items: readonly TrackedTransaction[], saved: readonly TrackedTransaction[]) {
  const byId = new Map(saved.map(item => [item.id, item]));
  for (const item of items) if (item.kind === "REDEEM") {
    if (item.phase === "CONFIRMED") byId.set(item.id, item);
    else byId.delete(item.id);
  }
  return [...byId.values()];
}

function same(a: string, b: string): boolean { return a.toLowerCase() === b.toLowerCase(); }
function errorText(error: unknown): string { return error instanceof Error ? error.message : "Transaction preparation could not be completed."; }
function isWalletRejection(error: unknown): boolean {
  let current = error;
  for (let depth = 0; depth < 6 && current && typeof current === "object"; depth++) {
    const value = current as { code?: unknown; cause?: unknown };
    if (value.code === 4001 || value.code === "4001") return true;
    current = value.cause;
  }
  return false;
}
